import { useEffect, useMemo, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import Reveal from "../components/Reveal";
import useDocumentTitle from "../hooks/useDocumentTitle";
import { findRoomSet, ROOM_SETS, formatEur, MATERIAL_BASELINE } from "../data/shop";
import { buildSetDatasheetPdf } from "../utils/shopDatasheet";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

export default function ShopDetail() {
  const { slug } = useParams();
  const set = useMemo(() => findRoomSet(slug), [slug]);
  const [qty, setQty] = useState(1);
  const [stickyVisible, setStickyVisible] = useState(false);

  // Show sticky bottom CTA on mobile after the user scrolls past ~60vh
  useEffect(() => {
    if (!set) return;
    const onScroll = () => {
      const y = window.scrollY;
      const threshold = window.innerHeight * 0.55;
      const nearBottom =
        window.innerHeight + y > document.documentElement.scrollHeight - 220;
      setStickyVisible(y > threshold && !nearBottom);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [set]);

  // Related: prefer same style first, then same room. Exclude self. Up to 3.
  const related = useMemo(() => {
    if (!set) return [];
    const sameStyle = ROOM_SETS.filter((s) => s.slug !== set.slug && s.style === set.style);
    const sameRoom = ROOM_SETS.filter(
      (s) => s.slug !== set.slug && s.room === set.room && !sameStyle.includes(s)
    );
    return [...sameStyle, ...sameRoom].slice(0, 3);
  }, [set]);

  useDocumentTitle(
    set ? `${set.name} — Shop` : "Set nicht gefunden",
    set
      ? `${set.name}: ${set.subtitle} Vollständiges Raumset von Livinora — Auswahl auf Anfrage. Richtpreis ab ${formatEur(set.priceFrom)}.`
      : undefined
  );

  if (!set) {
    return <Navigate to="/shop" replace />;
  }

  const inquiryHref = `/kontakt?source=shop&set=${encodeURIComponent(set.slug)}&style=${encodeURIComponent(
    set.style
  )}&qty=${encodeURIComponent(qty)}`;

  const pdfInquiryHref = `/kontakt?source=pdf_request&set=${encodeURIComponent(set.slug)}&style=${encodeURIComponent(
    set.style
  )}&qty=${encodeURIComponent(qty)}`;

  return (
    <div data-testid="page-shop-detail">
      {/* Header */}
      <section className="pt-28 sm:pt-32 md:pt-44 pb-12 sm:pb-16 md:pb-20">
        <div className="container-livinora">
          <div className="text-xs uppercase tracking-[0.18em] text-[color:var(--livinora-muted)]">
            <Link to="/shop" className="hover:text-[color:var(--livinora-ink)] transition-colors" data-testid="shop-detail-breadcrumb">
              Shop
            </Link>
            <span className="mx-2">/</span>
            <span className="text-[color:var(--livinora-ink)]">{set.style_label}</span>
          </div>

          <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
            {/* Image */}
            <div className="lg:col-span-7">
              <Reveal variant="mask">
                <div className="media-wrap aspect-[4/5]">
                  <img
                    src={set.image}
                    alt={set.name}
                    className="w-full h-full object-cover"
                    loading="eager"
                    data-testid="shop-detail-image"
                  />
                </div>
              </Reveal>
            </div>

            {/* Info */}
            <div className="lg:col-span-5 lg:sticky lg:top-28">
              <span className="micro-label">Standard-Set · {set.style_label}</span>
              <h1
                className="display-xl mt-5 text-[40px] sm:text-[52px] md:text-[64px] text-balance"
                data-testid="shop-detail-name"
              >
                {set.name}
              </h1>
              <p className="mt-6 serif italic text-xl sm:text-2xl text-[color:var(--livinora-ink-soft)] leading-snug">
                {set.subtitle}
              </p>
              <p className="prose-livinora mt-7">
                {set.description}
              </p>

              {/* Price block */}
              <div className="card-depth card-depth--warm mt-10 p-5 sm:p-7" data-testid="shop-detail-price">
                <div className="flex items-end justify-between gap-4 flex-wrap">
                  <div>
                    <div className="text-[9.5px] uppercase tracking-[0.28em] text-[color:var(--livinora-stone-deep)]">Richtpreis</div>
                    <div className="serif text-3xl sm:text-4xl mt-2 leading-none">
                      ab {formatEur(set.priceFrom)}
                    </div>
                  </div>
                  <div className="micro-label">
                    Set · {set.components.length} Komponenten
                  </div>
                </div>
                <p className="mt-5 text-xs text-[color:var(--livinora-muted)] leading-relaxed">
                  Richtpreis zur Orientierung. Lieferung, Montage, Mengen und projektspezifische
                  Anpassungen auf Anfrage.
                </p>
              </div>

              {/* Palette */}
              <div className="mt-8">
                <div className="overline">Palette</div>
                <div className="mt-3 flex gap-2">
                  {set.palette.map((c) => (
                    <span
                      key={c}
                      className="w-8 h-8 border border-[color:var(--livinora-border)]"
                      style={{ background: c }}
                    />
                  ))}
                </div>
              </div>

              {/* Suited for */}
              <div className="mt-8">
                <div className="overline">Geeignet für</div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {set.suitedFor.map((x) => (
                    <span
                      key={x}
                      className="text-xs uppercase tracking-[0.18em] px-3 py-2 border border-[color:var(--livinora-border)]"
                    >
                      {x}
                    </span>
                  ))}
                </div>
              </div>

              {/* Quantity */}
              <div className="mt-10 border-t border-[color:var(--livinora-border)] pt-8">
                <div className="overline mb-4">Anzahl Einheiten</div>
                <div className="flex items-center gap-4" data-testid="shop-detail-qty">
                  <button
                    type="button"
                    onClick={() => setQty((q) => Math.max(1, q - 1))}
                    aria-label="Anzahl verringern"
                    className="w-12 h-12 border border-[color:var(--livinora-border)] hover:border-[color:var(--livinora-ink)] transition-colors text-xl"
                    data-testid="shop-detail-qty-minus"
                  >
                    −
                  </button>
                  <input
                    type="number"
                    min="1"
                    max="999"
                    value={qty}
                    onChange={(e) => {
                      const v = parseInt(e.target.value, 10);
                      setQty(Number.isFinite(v) && v > 0 ? Math.min(v, 999) : 1);
                    }}
                    className="w-20 h-12 text-center border border-[color:var(--livinora-border)] bg-transparent serif text-xl focus:border-[color:var(--livinora-ink)] outline-none"
                    data-testid="shop-detail-qty-input"
                  />
                  <button
                    type="button"
                    onClick={() => setQty((q) => Math.min(999, q + 1))}
                    aria-label="Anzahl erhöhen"
                    className="w-12 h-12 border border-[color:var(--livinora-border)] hover:border-[color:var(--livinora-ink)] transition-colors text-xl"
                    data-testid="shop-detail-qty-plus"
                  >
                    +
                  </button>
                  <span className="text-xs uppercase tracking-[0.18em] text-[color:var(--livinora-muted)] ml-2">
                    {qty === 1 ? "1 Einheit" : `${qty} Einheiten`}
                  </span>
                </div>
                <p className="mt-4 text-xs text-[color:var(--livinora-muted)] leading-relaxed">
                  Mehrere Einheiten? Für Apartmentanlagen oder Ferienparks erstellen wir ein
                  Staffelungs-Konzept inkl. Lieferung, Montage und Standard-Variation.
                </p>
              </div>

              {/* CTAs */}
              <div className="mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4">
                <Link
                  to={inquiryHref}
                  className="btn-livinora btn-primary btn-magnetic w-full sm:w-auto justify-center sm:justify-start"
                  data-testid="shop-detail-inquiry"
                >
                  Set anfragen <span>→</span>
                </Link>
                <Link
                  to={pdfInquiryHref}
                  className="btn-livinora btn-ghost w-full sm:w-auto justify-center sm:justify-start"
                  data-testid="shop-detail-pdf-inquiry"
                >
                  PDF anfordern
                </Link>
              </div>
              <button
                type="button"
                onClick={() => buildSetDatasheetPdf(set)}
                className="mt-3 text-[11px] uppercase tracking-[0.2em] text-[color:var(--livinora-ink)] underline-offset-4 hover:underline"
                data-testid="shop-detail-pdf-download"
              >
                Datenblatt sofort als PDF herunterladen ↓
              </button>
              <p className="mt-5 text-xs text-[color:var(--livinora-muted)] leading-relaxed">
                Inquiry-first — keine direkte Bestellung. Verfügbarkeit, Preis und Lieferung
                bestätigen wir im persönlichen Angebot.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Components Accordion */}
      <section className="py-20 sm:py-24 md:py-28 bg-[color:var(--livinora-paper-warm)] border-t border-[color:var(--livinora-hairline)]">
        <div className="container-livinora grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <span className="micro-label">Materialität & Komponenten</span>
            <h2 className="display-xl mt-6 text-4xl md:text-5xl">
              Was im Set enthalten ist.
            </h2>
            <p className="prose-livinora mt-7 max-w-md">
              Eine vollständige Raum-Komposition. Möbel, Textilien und Licht — aufeinander
              abgestimmt, in einer Stilserie. Jede Position einklappbar mit Material, Maßen und
              Hinweis.
            </p>
            <button
              type="button"
              onClick={() => buildSetDatasheetPdf(set)}
              className="btn-line mt-9"
              data-testid="shop-detail-pdf-download-secondary"
            >
              Datenblatt herunterladen
            </button>
          </div>
          <div className="lg:col-span-8" data-testid="shop-detail-includes">
            <Accordion type="single" collapsible className="border-t border-[color:var(--livinora-hairline)]">
              {set.components.map((co, i) => (
                <AccordionItem
                  key={co.title + i}
                  value={`item-${i}`}
                  className="border-b border-[color:var(--livinora-hairline)]"
                >
                  <AccordionTrigger
                    className="hover:no-underline py-5 group"
                    data-testid={`shop-component-trigger-${i}`}
                  >
                    <span className="flex items-baseline gap-4 text-left flex-1">
                      <span className="number-tag w-10 shrink-0">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="serif text-xl sm:text-2xl leading-snug text-[color:var(--livinora-ink)] group-hover:text-[color:var(--livinora-ink)]">
                        {co.title}
                      </span>
                    </span>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="grid grid-cols-1 sm:grid-cols-12 gap-6 pl-0 sm:pl-14 pb-2">
                      <div className="sm:col-span-4">
                        <div className="media-wrap aspect-[4/3]">
                          <img
                            src={set.image}
                            alt={co.title}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      </div>
                      <div className="sm:col-span-8 space-y-3 text-sm">
                        {co.desc && (
                          <p className="text-[color:var(--livinora-muted)] leading-relaxed">
                            {co.desc}
                          </p>
                        )}
                        <SpecRow label="Material" value={co.material} />
                        <SpecRow label="Ausführung" value={co.finish} />
                        <SpecRow label="Maße" value={co.dimensions} />
                        {co.note && <SpecRow label="Hinweis" value={co.note} />}
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Material Specification — global baseline applied to all sets */}
      <section className="py-20 sm:py-24 md:py-28 border-t border-[color:var(--livinora-hairline)]" data-testid="shop-detail-material">
        <div className="container-livinora grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
          <div className="lg:col-span-4">
            <span className="micro-label">Konstruktion</span>
            <h2 className="display-xl mt-6 text-4xl md:text-5xl">
              Materialspezifikation.
            </h2>
            <p className="prose-livinora mt-7 max-w-md">
              Korpus, Türen und Schubladen aus FSC®-zertifiziertem Holzwerkstoff — Beschläge und
              Auszüge in Metall, Füße aus Massivholz. Eine konsistente Konstruktionslogik über
              alle Stilserien hinweg.
            </p>
          </div>
          <div className="lg:col-span-8">
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-10">
              {MATERIAL_BASELINE.map((row) => (
                <div
                  key={row.label}
                  className="flex items-baseline justify-between gap-4 py-4 border-b border-[color:var(--livinora-hairline)]"
                  data-testid={`material-row-${row.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  <dt className="text-[10px] uppercase tracking-[0.22em] text-[color:var(--livinora-stone-deep)]">
                    {row.label}
                  </dt>
                  <dd className="text-sm sm:text-base text-right text-[color:var(--livinora-ink)] max-w-[55%]">
                    {row.value}
                  </dd>
                </div>
              ))}
            </dl>
            <p className="mt-6 text-xs text-[color:var(--livinora-muted)] leading-relaxed">
              Stilserien-spezifische Furniere und Massivholz-Akzente (Eiche, Esche, Lärche, Räuchereiche)
              werden je Komponente in der obigen Übersicht ausgewiesen.
            </p>
          </div>
        </div>
      </section>

      {/* Related */}      {related.length > 0 && (
        <section className="py-20 sm:py-24 md:py-28 border-t border-[color:var(--livinora-hairline)]">
          <div className="container-livinora">
            <div className="flex items-end justify-between flex-wrap gap-4">
              <div>
                <span className="micro-label">Weitere Sets</span>
                <h2 className="display-xl mt-6 text-4xl md:text-5xl">
                  Passende Kompositionen.
                </h2>
              </div>
              <Link to="/shop" className="btn-line">
                Alle Sets ansehen
              </Link>
            </div>
            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-12">
              {related.map((s) => (
                <Link
                  key={s.slug}
                  to={`/shop/${s.slug}`}
                  className="group block"
                  data-testid={`shop-related-${s.slug}`}
                >
                  <div className="media-wrap aspect-[4/5]">
                    <img src={s.image} alt={s.name} className="w-full h-full object-cover" loading="lazy" />
                  </div>
                  <div className="mt-5 flex items-baseline justify-between gap-4">
                    <span className="micro-label">{s.style_label}</span>
                    <span className="text-[10.5px] uppercase tracking-[0.22em] text-[color:var(--livinora-stone-deep)]">
                      ab {formatEur(s.priceFrom)}
                    </span>
                  </div>
                  <h3 className="serif mt-3 text-2xl leading-[1.05] transition-transform duration-700 group-hover:translate-x-1">{s.name}</h3>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Mobile sticky CTA — appears after scrolling past hero, hidden on lg+ */}
      <div
        className={`shop-sticky-cta lg:hidden fixed left-0 right-0 z-40 transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${
          stickyVisible
            ? "opacity-100 translate-y-0 pointer-events-auto"
            : "opacity-0 translate-y-6 pointer-events-none"
        }`}
        style={{
          bottom: "calc(env(safe-area-inset-bottom, 0px))",
        }}
        data-testid="shop-detail-sticky-cta"
        aria-hidden={!stickyVisible}
      >
        <div className="mx-3 mb-3 bg-[color:var(--livinora-paper)]/95 backdrop-blur-md border border-[color:var(--livinora-border)] shadow-[0_18px_44px_-18px_rgba(0,0,0,0.35)] flex items-center gap-3 px-4 py-3">
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[color:var(--livinora-muted)]">
              Richtpreis
            </div>
            <div className="serif text-lg leading-tight truncate text-[color:var(--livinora-ink)]">
              ab {formatEur(set.priceFrom)}
            </div>
          </div>
          <Link
            to={inquiryHref}
            className="btn-livinora btn-primary !min-h-[48px] whitespace-nowrap"
            data-testid="shop-detail-sticky-cta-inquiry"
          >
            Set anfragen <span>→</span>
          </Link>
        </div>
      </div>
    </div>
  );
}

function SpecRow({ label, value }) {
  if (!value) return null;
  return (
    <div className="grid grid-cols-12 gap-3">
      <span className="col-span-4 sm:col-span-3 text-[10px] uppercase tracking-[0.18em] text-[color:var(--livinora-muted)] pt-1">
        {label}
      </span>
      <span className="col-span-8 sm:col-span-9 text-[color:var(--livinora-ink)]">{value}</span>
    </div>
  );
}
